import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/utils';

const badgeVariants = cva(
  'inline-flex shrink-0 items-center justify-center overflow-hidden text-xs font-medium leading-4 [&_svg]:pointer-events-none [&_svg]:size-3 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground',
        secondary: 'bg-secondary text-secondary-foreground',
        outline: 'border border-border bg-background text-foreground',
        destructive: 'bg-destructive text-destructive-foreground',
        destructiveOutline: 'border border-destructive bg-background text-destructive',
      },
      shape: {
        /** Label row: ~8px radius, horizontal padding, optional icon gaps (Figma). */
        pill: 'gap-1 rounded-md px-2 py-0.5',
        /** Count / compact: 20px min height, pill circle (Figma). */
        counter: 'h-5 min-w-5 gap-0 rounded-full px-1 tabular-nums',
      },
    },
    defaultVariants: {
      variant: 'default',
      shape: 'pill',
    },
  }
);

export interface BadgeProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {
  asChild?: boolean;
  /** Optional leading icon or glyph (12px slot). */
  leftIcon?: ReactNode;
  /** Optional trailing icon or glyph (12px slot). */
  rightIcon?: ReactNode;
}

const Badge = forwardRef<HTMLDivElement, BadgeProps>(
  (
    { className, variant, shape, asChild = false, leftIcon, rightIcon, children, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : 'div';
    return (
      <Comp ref={ref} className={cn(badgeVariants({ variant, shape }), className)} {...props}>
        {leftIcon}
        {children}
        {rightIcon}
      </Comp>
    );
  }
);
Badge.displayName = 'Badge';

export { Badge, badgeVariants };
